import type {
  AiModelProfileDto,
  CopilotPromptConfigurationsDto,
  ILoqCredentialsDto,
  ILoqProjectConfigurationDto,
  RemsProjectConfigurationDto,
  RemsProjectConfigurationRequest,
} from "@/api/types";

import type { RemsBulkEditableField } from "./configurationDefinitions";
import type { PortfolioIntegrationProject, PortfolioIntegrationProjectStatus } from "./types";

export type CredentialMode = "keep" | "missing" | "replace";
export type ILoqOperation = "configure" | "enable" | "disable";
export type RemsOperation = "configure" | "enableTickets" | "enableContracts" | "disable";

export type ILoqBulkEditableValues = {
  serviceAddress: string;
  customerCode: string;
  username: string;
  password: string;
};

export type PreparedBulkProject<TPayload> = {
  id: string;
  name: string;
  payload: TPayload;
  reason?: string;
};

export const CONFIGURATION_REQUEST_CONCURRENCY = 6;

export type AiConfigurationChanges = {
  communityPrompt?: string;
  ticketPrompt?: string;
  profile?: AiModelProfileDto;
};

/** Merges explicit portfolio AI changes while preserving every unselected per-project value. */
export function prepareAiConfigurations(
  current: CopilotPromptConfigurationsDto,
  changes: AiConfigurationChanges,
): CopilotPromptConfigurationsDto {
  const configurations = new Map(current.configurations.map((configuration) => [configuration.type, configuration]));
  const applyPrompt = (type: "community" | "ticket", prompt: string) => {
    const existing = configurations.get(type);
    configurations.set(type, {
      type,
      prompt: prompt.trim(),
      profile: changes.profile ?? existing?.profile ?? "efficient",
    });
  };

  if (changes.communityPrompt !== undefined) {
    applyPrompt("community", changes.communityPrompt);
  }
  if (changes.ticketPrompt !== undefined) {
    applyPrompt("ticket", changes.ticketPrompt);
  }
  if (changes.profile !== undefined) {
    for (const [type, configuration] of configurations) {
      configurations.set(type, { ...configuration, profile: changes.profile });
    }
  }

  return { configurations: [...configurations.values()] };
}

export function prepareAiProject(
  row: PortfolioIntegrationProject<CopilotPromptConfigurationsDto>,
  changes: AiConfigurationChanges,
  messages: { notConfigured: string },
): PreparedBulkProject<CopilotPromptConfigurationsDto> {
  const payload = prepareAiConfigurations(row.configuration, changes);
  const isProfileOnlyChange =
    changes.profile !== undefined && changes.communityPrompt === undefined && changes.ticketPrompt === undefined;

  return {
    id: row.project.id,
    name: row.project.name,
    payload,
    reason: isProfileOnlyChange && payload.configurations.length === 0 ? messages.notConfigured : undefined,
  };
}

type PrepareILoqProjectOptions = {
  row: PortfolioIntegrationProject<ILoqProjectConfigurationDto>;
  operation: ILoqOperation;
  credentialMode: CredentialMode;
  values: ILoqBulkEditableValues;
  connectionCredentials: ILoqCredentialsDto;
  lockGroupId: string;
  realEstateId: string;
  zoneId: string;
  messages: { notConfigured: string; incomplete: string; password: string; mappings: string };
};

export function getPortfolioIntegrationProjectStatus({
  configured,
  enabled,
  setupComplete,
}: {
  configured: boolean;
  enabled: boolean;
  setupComplete: boolean;
}): PortfolioIntegrationProjectStatus {
  if (!configured) {
    return "notInUse";
  }
  if (!setupComplete) {
    return "setupIncomplete";
  }

  return enabled ? "enabled" : "disabled";
}

export function isRemsConfigured(configuration: RemsProjectConfigurationDto): boolean {
  return Boolean(
    configuration.tickets.enabled ||
    configuration.contracts.enabled ||
    configuration.tenantId ||
    configuration.communityUserContactId ||
    configuration.communityUserRelationId ||
    configuration.tickets.defectCode ||
    configuration.complexCode ||
    configuration.buildingComplexCodes.length,
  );
}

export function isRemsSetupComplete(configuration: RemsProjectConfigurationDto): boolean {
  return Boolean(
    configuration.tenantId?.trim() &&
    configuration.communityUserContactId?.trim() &&
    configuration.communityUserRelationId?.trim() &&
    (configuration.complexCode?.trim() || configuration.buildingComplexCodes.some((x) => x.complexCode?.trim())) &&
    (!configuration.tickets.enabled || configuration.tickets.defectCode?.trim()),
  );
}

export function isILoqConfigured(configuration: ILoqProjectConfigurationDto): boolean {
  return Boolean(configuration.serviceAddress?.trim());
}

export function isILoqSetupComplete(configuration: ILoqProjectConfigurationDto): boolean {
  return Boolean(
    configuration.serviceAddress?.trim() &&
    configuration.customerCode?.trim() &&
    configuration.username?.trim() &&
    configuration.lockGroupId?.trim() &&
    configuration.realEstateId?.trim() &&
    configuration.zoneId?.trim() &&
    configuration.securityAccessMappings.length,
  );
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Response) {
    return `${error.status} ${error.statusText}`.trim();
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

export function commonValue(values: Array<string | null | undefined>): string {
  const normalized = values.map((value) => value ?? "");

  return normalized.length > 0 && normalized.every((value) => value === normalized[0]) ? normalized[0] : "";
}

export async function mapWithConcurrency<TItem, TResult>(
  items: TItem[],
  concurrency: number,
  mapper: (item: TItem, index: number) => Promise<TResult>,
): Promise<TResult[]> {
  const results = new Array<TResult>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index], index);
    }
  };

  const workerCount = Math.min(Math.max(1, Math.floor(concurrency) || 1), items.length);
  await Promise.all(Array.from({ length: workerCount }, worker));

  return results;
}

export function prepareRemsProject(
  row: PortfolioIntegrationProject<RemsProjectConfigurationDto>,
  operation: RemsOperation,
  changedFields: Record<RemsBulkEditableField, boolean>,
  values: Record<RemsBulkEditableField, string>,
  messages: { identifiers: string; complexCode: string; defectCode: string; emptySharedValue: string },
): PreparedBulkProject<RemsProjectConfigurationRequest> {
  const configuration = row.configuration;
  const payload: RemsProjectConfigurationRequest = {
    tickets: { ...configuration.tickets },
    contracts: { ...configuration.contracts },
    tenantId: changedFields.tenantId ? values.tenantId.trim() : configuration.tenantId,
    communityUserContactId: changedFields.communityUserContactId
      ? values.communityUserContactId.trim()
      : configuration.communityUserContactId,
    communityUserRelationId: changedFields.communityUserRelationId
      ? values.communityUserRelationId.trim()
      : configuration.communityUserRelationId,
    complexCode: configuration.complexCode,
    buildingComplexCodes: configuration.buildingComplexCodes,
  };
  payload.tickets.defectCode = changedFields.defectCode ? values.defectCode.trim() : configuration.tickets.defectCode;

  if (operation === "enableTickets") {
    payload.tickets.enabled = true;
  } else if (operation === "enableContracts") {
    payload.contracts.enabled = true;
  } else if (operation === "disable") {
    payload.tickets = {
      ...payload.tickets,
      enabled: false,
      syncClosedStatus: false,
      sendTicketUpdates: false,
      autoSendTickets: false,
    };
    payload.contracts = { enabled: false, syncContacts: false };
  }

  let reason: string | undefined;
  const hasBlankSharedValue = (Object.keys(changedFields) as RemsBulkEditableField[]).some(
    (field) => changedFields[field] && !values[field].trim(),
  );
  if (hasBlankSharedValue) {
    reason = messages.emptySharedValue;
  } else if (payload.tickets.enabled || payload.contracts.enabled) {
    if (
      !payload.tenantId?.trim() ||
      !payload.communityUserContactId?.trim() ||
      !payload.communityUserRelationId?.trim()
    ) {
      reason = messages.identifiers;
    } else if (
      !payload.complexCode?.trim() &&
      !payload.buildingComplexCodes?.some((code) => code.complexCode?.trim())
    ) {
      reason = messages.complexCode;
    } else if (payload.tickets.enabled && !payload.tickets.defectCode?.trim()) {
      reason = messages.defectCode;
    }
  }

  return { id: row.project.id, name: row.project.name, payload, reason };
}

export function prepareILoqProject({
  row,
  operation,
  credentialMode,
  values,
  connectionCredentials,
  lockGroupId,
  realEstateId,
  zoneId,
  messages,
}: PrepareILoqProjectOptions): PreparedBulkProject<ILoqProjectConfigurationDto> {
  const configuration = row.configuration;
  const replaceCredentials = credentialMode === "replace" || (credentialMode === "missing" && !row.configured);
  const usesConnectionAccount =
    replaceCredentials ||
    (configuration.serviceAddress?.trim() === connectionCredentials.serviceAddress.trim() &&
      configuration.customerCode?.trim() === connectionCredentials.customerCode.trim() &&
      configuration.username?.trim() === connectionCredentials.username.trim());
  let enabled = configuration.enabled;
  if (operation === "enable") {
    enabled = true;
  } else if (operation === "disable") {
    enabled = false;
  }
  const payload: ILoqProjectConfigurationDto = {
    ...configuration,
    enabled,
    serviceAddress: replaceCredentials ? values.serviceAddress.trim() : configuration.serviceAddress,
    customerCode: replaceCredentials ? values.customerCode.trim() : configuration.customerCode,
    username: replaceCredentials ? values.username.trim() : configuration.username,
    password: replaceCredentials ? values.password : undefined,
    lockGroupId: usesConnectionAccount ? lockGroupId || configuration.lockGroupId : configuration.lockGroupId,
    realEstateId: usesConnectionAccount ? realEstateId || configuration.realEstateId : configuration.realEstateId,
    zoneId: usesConnectionAccount ? zoneId || configuration.zoneId : configuration.zoneId,
    supportUserId: configuration.supportUserId,
    securityAccessMappings: configuration.securityAccessMappings,
  };

  let reason: string | undefined;
  if (operation === "disable") {
    reason = row.configured ? undefined : messages.notConfigured;
  } else if (
    !payload.serviceAddress?.trim() ||
    !payload.customerCode?.trim() ||
    !payload.username?.trim() ||
    !payload.lockGroupId?.trim() ||
    !payload.realEstateId?.trim() ||
    !payload.zoneId?.trim()
  ) {
    reason = messages.incomplete;
  } else if (replaceCredentials && !payload.password?.trim()) {
    reason = messages.password;
  } else if (operation === "enable" && payload.securityAccessMappings.length === 0) {
    reason = messages.mappings;
  }

  return { id: row.project.id, name: row.project.name, payload, reason };
}
